import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge, TreatmentList } from "@/components/workflow/ui";
import { vitalAbnormal } from "@/lib/vitals";
import {
  Baby, Pill, BookMarked, Loader2, CheckCircle2, Download, Save, FileText, RotateCcw, ClipboardList,
} from "lucide-react";

const SIGN_STATES = ["Present", "Absent", "Unsure"];

function signBtnClass(st, active) {
  if (!active) return "bg-white border-zinc-300 text-zinc-600 hover:border-zinc-400";
  if (st === "Present") return "bg-[#002FA7] border-[#002FA7] text-white";
  if (st === "Absent") return "bg-zinc-900 border-zinc-900 text-white";
  return "bg-amber-500 border-amber-500 text-white";
}

export function SignsChecklist({ signs, signResp, setSignResp, reminded }) {
  return (
    <div className="space-y-px bg-zinc-200 border border-zinc-200 rounded-md overflow-hidden">
      {signs.signs?.map((s, i) => {
        const isNeg = (s.type || "").toLowerCase() === "negative";
        const cur = signResp[s.id || i]?.status;
        const unmarked = reminded && (!cur || cur === "Not assessed");
        return (
        <div key={s.id || i} className={`p-4 sm:flex sm:items-start sm:justify-between gap-4 ${unmarked ? "bg-amber-50" : "bg-white"}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-zinc-900">{s.sign}</p>
              {s.type && (
                <span
                  data-testid={`sign-type-${i}`}
                  className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${isNeg ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                >
                  {isNeg ? "Rule-out" : "Confirm +"}
                </span>
              )}
            </div>
            {s.discriminates && <p className="text-xs text-zinc-400 mt-0.5">{s.discriminates}</p>}
          </div>
          <div className="mt-3 sm:mt-0 flex gap-1.5 shrink-0">
            {SIGN_STATES.map((st) => (
              <button
                key={st} type="button"
                data-testid={`sign-${i}-${st.toLowerCase()}`}
                onClick={() => setSignResp((r) => ({ ...r, [s.id || i]: { ...r[s.id || i], status: st } }))}
                className={["px-3 py-1 rounded border text-xs font-medium transition-colors", signBtnClass(st, signResp[s.id || i]?.status === st)].join(" ")}
              >{st}</button>
            ))}
          </div>
        </div>
        );
      })}
    </div>
  );
}

export function VitalsGrid({ vitalCfg, vitals, setFindings }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {vitalCfg.map((v) => {
        const val = vitals[v.k] || "";
        const abn = vitalAbnormal(v, val);
        return (
          <div key={v.k} className="space-y-1.5">
            <Label className="text-xs">{v.label} <span className="text-zinc-400">({v.unit})</span></Label>
            <Input
              data-testid={`vital-${v.k}`}
              inputMode="decimal"
              value={val}
              onChange={(e) => setFindings((f) => ({ ...f, vitals: { ...f.vitals, [v.k]: e.target.value } }))}
              className={abn ? "border-red-500 text-red-700 focus-visible:ring-red-500" : ""}
            />
          </div>
        );
      })}
    </div>
  );
}

export function PediatricHistoryFields({ patient, updPatient }) {
  return (
    <div className="mt-6 border border-dashed border-[#002FA7]/40 rounded-md p-5 bg-blue-50/30" data-testid="pediatric-history-section">
      <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Baby className="h-4 w-4 text-[#002FA7]" /> Pediatric case sheet</p>
      <p className="text-xs text-zinc-500 mt-0.5">Birth / obstetric and immunization history inform pediatric diagnosis.</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Birth / obstetric history</Label>
          <Textarea rows={3} data-testid="patient-obstetric" value={patient.obstetric_history} onChange={updPatient("obstetric_history")} placeholder="Antenatal course, gestation, mode & place of delivery, birth weight, perinatal/NICU events, feeding & developmental milestones…" />
        </div>
        <div className="space-y-1.5">
          <Label>Immunization history</Label>
          <Textarea rows={3} data-testid="patient-vaccination" value={patient.vaccination_history} onChange={updPatient("vaccination_history")} placeholder="Up-to-date per schedule? BCG, OPV/IPV, DPT, Hib, Hep-B, MMR, boosters; any missed/delayed vaccines…" />
        </div>
      </div>
    </div>
  );
}

export function TreatmentPlan({ treatment }) {
  const t = treatment || {};
  return (
    <div className="mt-8" data-testid="treatment-plan">
      <div className="flex items-center gap-2 text-[#002FA7] mb-3"><Pill className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">Treatment plan</span></div>
      {t.overview && <p className="text-sm text-zinc-700 mb-5">{t.overview}</p>}
      {t.pharmacological?.length > 0 && (
        <div className="border border-zinc-200 rounded-md overflow-hidden mb-5">
          <div className="bg-zinc-900 text-white text-xs font-semibold uppercase tracking-wider px-4 py-2">Pharmacological</div>
          {t.pharmacological.map((d, i) => (
            <div key={d.drug || i} className="p-4 border-t border-zinc-200 first:border-t-0">
              <p className="font-medium text-zinc-900">{d.drug}</p>
              <p className="text-sm text-zinc-600">{d.dose}{d.duration ? ` · ${d.duration}` : ""}</p>
              {d.note && <p className="text-xs text-zinc-400 mt-1">{d.note}</p>}
            </div>
          ))}
        </div>
      )}
      <TreatmentList title="Non-pharmacological" items={t.non_pharmacological} />
      <TreatmentList title="Monitoring" items={t.monitoring} />
      <TreatmentList title="Red flags & referral" items={t.red_flags_referral} danger />
    </div>
  );
}

export function EvidenceReferences({ references }) {
  if (!references?.length) return null;
  return (
    <div className="mt-8" data-testid="evidence-references">
      <div className="flex items-center gap-2 text-[#002FA7] mb-3"><BookMarked className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">Evidence references</span></div>
      <ul className="space-y-2">
        {references.map((r, i) => (
          <li key={r.citation || r.source || i} className="text-sm text-zinc-700 border-l-2 border-[#002FA7] pl-3">
            <span className="font-semibold">{r.source}:</span> {r.citation}
          </li>
        ))}
      </ul>
    </div>
  );
}

function saveButtonIcon(savingCase, savedId) {
  if (savingCase) return <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
  if (savedId) return <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />;
  return <Save className="mr-2 h-4 w-4" />;
}

function saveButtonLabel(savedId, user) {
  if (savedId) return "Saved to history";
  if (user) return "Save to history";
  return "Sign in to save";
}

export function FinalActions({ user, downloading, downloadingRx, downloadingSheet, savingCase, savedId, downloadPdf, downloadRx, downloadCaseSheet, saveCase, reset }) {
  return (
    <div className="mt-10 flex flex-wrap gap-3">
      <Button onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn" className="bg-[#002FA7] hover:bg-[#002687] text-white h-11 px-6">
        {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
        Patient report (PDF)
      </Button>
      <Button onClick={downloadRx} disabled={downloadingRx} data-testid="download-rx-btn" variant="outline" className="border-[#002FA7] text-[#002FA7] hover:bg-blue-50 h-11 px-6">
        {downloadingRx ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
        Prescription (PDF)
      </Button>
      <Button onClick={downloadCaseSheet} disabled={downloadingSheet} data-testid="download-casesheet-btn" variant="outline" className="border-[#002FA7] text-[#002FA7] hover:bg-blue-50 h-11 px-6">
        {downloadingSheet ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-2 h-4 w-4" />}
        Full case sheet (PDF)
      </Button>
      <Button onClick={saveCase} disabled={savingCase || !!savedId || !user} data-testid="save-case-btn" variant="outline" className="border-zinc-300 h-11 px-6">
        {saveButtonIcon(savingCase, savedId)}
        {saveButtonLabel(savedId, user)}
      </Button>
      <Button onClick={reset} data-testid="new-case-btn" variant="ghost" className="text-zinc-500 h-11 px-6">
        <RotateCcw className="mr-2 h-4 w-4" /> New case
      </Button>
    </div>
  );
}

// Badge is re-exported for consumers that compose final-diagnosis header.
export { Badge };
